import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import ConsentBanner from "@/components/ConsentBanner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Kit";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import Layout from "@/components/Layout";

const Welcome = lazy(() => import("@/pages/Welcome"));
const Console = lazy(() => import("@/pages/Console"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AccessConsent = lazy(() => import("@/pages/AccessConsent"));
const Sso = lazy(() => import("@/pages/Sso"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const PunchMobile = lazy(() => import("@/pages/PunchMobile"));
const Employees = lazy(() => import("@/pages/Employees"));
const EmployeeDetail = lazy(() => import("@/pages/EmployeeDetail"));
const Timesheets = lazy(() => import("@/pages/Timesheets"));
const TimesheetDetail = lazy(() => import("@/pages/TimesheetDetail"));
const Approvals = lazy(() => import("@/pages/Approvals"));
const Chantiers = lazy(() => import("@/pages/Chantiers"));
const Projects = lazy(() => import("@/pages/Projects"));
const CostCodes = lazy(() => import("@/pages/CostCodes"));
const PayrollExport = lazy(() => import("@/pages/PayrollExport"));
const Reports = lazy(() => import("@/pages/Reports"));
const Security = lazy(() => import("@/pages/Security"));
const Loi25 = lazy(() => import("@/pages/Loi25"));
const Backups = lazy(() => import("@/pages/Backups"));
const Legal = lazy(() => import("@/pages/Legal"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const CloudConnect = lazy(() => import("@/pages/CloudConnect"));
const CloudActivations = lazy(() => import("@/pages/CloudActivations"));
const Settings = lazy(() => import("@/pages/Settings"));
const Companies = lazy(() => import("@/pages/Companies"));
const Subscription = lazy(() => import("@/pages/Subscription"));
const ModulePlaceholder = lazy(() => import("@/pages/ModulePlaceholder"));
const CcqCompliance = lazy(() => import("@/pages/CcqCompliance"));
const Maintenance = lazy(() => import("@/pages/Maintenance"));
const AccountantRequests = lazy(() => import("@/pages/AccountantRequests"));
const Releases = lazy(() => import("@/pages/Releases"));
const Conditions = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.Conditions })));
const Temoins = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.Temoins })));
const MentionsLegales = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.MentionsLegales })));
const Securite = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.Securite })));
const Accessibilite = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.Accessibilite })));
const PrivacyRequest = lazy(() => import("@/pages/LegalPages").then((m) => ({ default: m.PrivacyRequest })));

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div style={{ minHeight: "100vh" }}><Loader label="Chargement de PROGÈRE PUNCH…" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.technician && !user.platform_admin && loc.pathname !== "/maintenance")
    return <Navigate to="/maintenance" replace />;
  return children;
}

function RoleRoute({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <div style={{ minHeight: "100vh" }}><Loader /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/tableau" replace />;
  return children;
}

const Eco = ({ name }) => <RoleRoute roles={["admin"]}><ModulePlaceholder name={name} /></RoleRoute>;

function PlatformRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <div style={{ minHeight: "100vh" }}><Loader /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.platform_admin) return <Navigate to="/tableau" replace />;
  return children;
}

function PlatformMemberRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <div style={{ minHeight: "100vh" }}><Loader /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.platform_admin && !user.technician) return <Navigate to="/tableau" replace />;
  return children;
}

function Shell() {
  return (
    <Suspense fallback={<div style={{ minHeight: "100vh" }}><Loader label="Chargement…" /></div>}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Home />} />
      <Route path="/bienvenue" element={<Welcome />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
      <Route path="/reinitialiser" element={<ResetPassword />} />
      <Route path="/definir-mot-de-passe" element={<ResetPassword />} />
      <Route path="/confidentialite" element={<Legal />} />
      <Route path="/conditions" element={<Conditions />} />
      <Route path="/temoins" element={<Temoins />} />
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/securite-plateforme" element={<Securite />} />
      <Route path="/accessibilite" element={<Accessibilite />} />
      <Route path="/demande-confidentialite" element={<PrivacyRequest />} />
      <Route path="/se-connecter" element={<Navigate to="/login" replace />} />
      <Route path="/consentement-acces" element={<AccessConsent />} />
      <Route path="/sso" element={<Sso />} />
      <Route path="/sso/consume" element={<Sso />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/tableau" element={<Dashboard />} />
        <Route path="/console" element={<PlatformRoute><Console /></PlatformRoute>} />
        <Route path="/maintenance" element={<PlatformMemberRoute><Maintenance /></PlatformMemberRoute>} />
        <Route path="/cloud-activations" element={<PlatformMemberRoute><CloudActivations /></PlatformMemberRoute>} />
        <Route path="/punch-mobile" element={<PunchMobile />} />
        <Route path="/employes" element={<RoleRoute roles={["foreman", "project_manager", "admin", "comptable"]}><Employees /></RoleRoute>} />
        <Route path="/employes/:id" element={<RoleRoute roles={["foreman", "project_manager", "admin", "comptable"]}><EmployeeDetail /></RoleRoute>} />
        <Route path="/feuilles" element={<Timesheets />} />
        <Route path="/feuilles/:id" element={<TimesheetDetail />} />
        <Route path="/approbations" element={<RoleRoute roles={["foreman", "project_manager", "admin"]}><Approvals /></RoleRoute>} />
        <Route path="/chantiers" element={<Chantiers />} />
        <Route path="/projets" element={<RoleRoute roles={["foreman", "project_manager", "admin"]}><Projects /></RoleRoute>} />
        <Route path="/codes-cout" element={<RoleRoute roles={["project_manager", "admin", "comptable"]}><CostCodes /></RoleRoute>} />
        <Route path="/export-paie" element={<RoleRoute roles={["admin", "comptable"]}><PayrollExport /></RoleRoute>} />
        <Route path="/rapports" element={<RoleRoute roles={["foreman", "project_manager", "admin", "comptable"]}><Reports /></RoleRoute>} />
        <Route path="/securite" element={<RoleRoute roles={["admin"]}><Security /></RoleRoute>} />
        <Route path="/loi25" element={<RoleRoute roles={["admin"]}><Loi25 /></RoleRoute>} />
        <Route path="/conformite-ccq" element={<RoleRoute roles={["foreman", "project_manager", "admin", "comptable"]}><CcqCompliance /></RoleRoute>} />
        <Route path="/sauvegardes" element={<RoleRoute roles={["admin"]}><Backups /></RoleRoute>} />
        <Route path="/integrations" element={<RoleRoute roles={["admin", "project_manager", "comptable"]}><Integrations /></RoleRoute>} />
        <Route path="/cloud-connect" element={<RoleRoute roles={["admin", "project_manager", "comptable"]}><CloudConnect /></RoleRoute>} />
        <Route path="/demandes-comptable" element={<RoleRoute roles={["admin"]}><AccountantRequests /></RoleRoute>} />
        <Route path="/parametres" element={<RoleRoute roles={["admin"]}><Settings /></RoleRoute>} />
        <Route path="/entreprises" element={<RoleRoute roles={["admin"]}><Companies /></RoleRoute>} />
        <Route path="/abonnement" element={<RoleRoute roles={["admin"]}><Subscription /></RoleRoute>} />
        <Route path="/nouveautes" element={<Releases />} />
        <Route path="/accueil" element={<Eco name="PROGÈRE ACCUEIL" />} />
        <Route path="/erp" element={<Eco name="PROGÈRE ERP" />} />
        <Route path="/paie" element={<Eco name="PROGÈRE PAIE" />} />
        <Route path="/compta" element={<Eco name="PROGÈRE COMPTA" />} />
        <Route path="/devis" element={<Eco name="PROGÈRE DEVIS AI" />} />
        <Route path="/crm" element={<Eco name="PROGÈRE CRM" />} />
        <Route path="/chantier" element={<Eco name="PROGÈRE CHANTIER" />} />
        <Route path="/documents" element={<Eco name="PROGÈRE DOCUMENTS" />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Toaster theme="dark" position="top-right" richColors />
        <BrowserRouter>
          <Shell />
          <ConsentBanner />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
